import { useCallback, useEffect, useMemo, useState } from "react";
import type { SavedView } from "./DataTable.types";
import { createLocalViewStore, type ViewStore } from "./viewStore";

export type UseSavedViewsOptions = {
  /** Optional read adapter. Defaults to the localStorage-backed store for `screenName`. */
  store?: ViewStore;
  /** Set false to keep the hook idle while a screen key is still resolving. */
  enabled?: boolean;
};

export type UseSavedViewsResult = {
  views: SavedView[];
  primaryView: SavedView | null;
  loading: boolean;
  error: unknown;
  refresh: () => Promise<SavedView[]>;
};

/**
 * Read saved views for a screen without exposing the saved-view HTTP helpers.
 *
 * Components that own the backend sync write into the same screen-scoped local
 * store; this hook subscribes to that store so consumers can react to the latest
 * mirrored list.
 */
export function useSavedViews(screenName: string | undefined, options: UseSavedViewsOptions = {}): UseSavedViewsResult {
  const { store, enabled = true } = options;
  const resolvedStore = useMemo(() => {
    if (!screenName) return undefined;
    return store ?? createLocalViewStore(screenName);
  }, [screenName, store]);
  const [views, setViews] = useState<SavedView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const refresh = useCallback(async () => {
    if (!enabled || !resolvedStore) {
      setViews([]);
      setLoading(false);
      setError(null);
      return [];
    }

    setLoading(true);
    setError(null);
    try {
      const next = await resolvedStore.list();
      setViews(next);
      return next;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [enabled, resolvedStore]);

  useEffect(() => {
    if (!enabled || !resolvedStore) {
      setViews([]);
      setLoading(false);
      setError(null);
      return;
    }

    let active = true;
    const load = () => {
      setLoading(true);
      setError(null);
      void resolvedStore
        .list()
        .then((next) => {
          if (active) setViews(next);
        })
        .catch((err) => {
          if (active) setError(err);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    };

    load();
    const unsubscribe = resolvedStore.subscribe(load);
    return () => {
      active = false;
      unsubscribe();
    };
  }, [enabled, resolvedStore]);

  const primaryView = useMemo(() => views.find((view) => view.is_default) ?? null, [views]);

  return { views, primaryView, loading, error, refresh };
}
